import {useEffect, useState} from "react";
import AccountNav from "../components/AccountNav";
import Modal from "../components/Modal";
import {
    adminDeleteProperty,
    adminEditProperty,
    approveProperty,
    getAdminPublicInquiries,
    getPendingProperties,
    rejectProperty,
    sharePublicInquiryWithPublisher,
    verifyProperty
} from "../services/api";
function getConfirmationCopy(dialog) {
    if (!dialog?.row) {
        return "";
    }

    if (dialog.type === "approve") {
        return `Approve "${dialog.row.title}" and publish it live?`;
    }

    if (dialog.type === "reject") {
        return `Reject "${dialog.row.title}" and keep it hidden from live listings?`;
    }

    return `Delete "${dialog.row.title}" permanently from the queue?`;
}

export default function AdminQueue() {
    const [rows, setRows] = useState([]);
    const [publicInquiries, setPublicInquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [dialog, setDialog] = useState(null);
    const [editForm, setEditForm] = useState({title: "", city: "", price: ""});
    const [submitting, setSubmitting] = useState(false);

    const loadQueue = async () => {
        try {
            setLoading(true);
            setError("");
            const [data, leadData] = await Promise.all([getPendingProperties(), getAdminPublicInquiries()]);
            setRows(Array.isArray(data) ? data : []);
            setPublicInquiries(Array.isArray(leadData) ? leadData : []);
        } catch (requestError) {
            const message = requestError?.response?.data?.detail || "Failed to load pending queue.";
            setError(message);
        } finally {
            setLoading(false);
        }

    };
    console.log("rows", rows); // add this line

    useEffect(() => {
        loadQueue();
    }, []);

    const closeDialog = () => {
        if (!submitting) {
            setDialog(null);
        }
    };

    const runAction = async (handler) => {
        try {
            setSubmitting(true);
            await handler();
            setDialog(null);
            await loadQueue();
        } catch (requestError) {
            const message = requestError?.response?.data?.detail || "Action failed.";
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const openEditDialog = (row) => {
        setEditForm({
            title: row.title || "",
            city: row.city || "",
            price: row.price || ""
        });
        setDialog({type: "edit", row});
    };

    const saveEdit = async () => {
        if (!dialog?.row) {
            return;
        }

        await runAction(() =>
            adminEditProperty(dialog.row.id, {
                title: editForm.title.trim(),
                city: editForm.city.trim() || null,
                price: Number(editForm.price)
            })
        );
    };

    const confirmAction = async () => {
        if (!dialog?.row) {
            return;
        }

        if (dialog.type === "approve") {
            await runAction(() => approveProperty(dialog.row.id));
            return;
        }

        if (dialog.type === "reject") {
            await runAction(() => rejectProperty(dialog.row.id));
            return;
        }

        await runAction(() => adminDeleteProperty(dialog.row.id));
    };

    const queueSummary = {
        pending: rows.length,
        submitters: new Set(rows.map((row) => row.owner?.email || row.owner_id)).size,
        averagePrice:
            rows.length > 0
                ? rows.reduce((sum, row) => sum + Number(row.price || row.min_price || row.max_price || 0), 0) / rows.length
                : 0
    };

    return (
        <div className="container page account-page">
            <div className="account-shell">
                <AccountNav/>

                <section className="account-main">
                    <div className="account-hero compact">
                        <div>
                            <h2>Admin Approval Queue</h2>
                            <p>Review pending submissions from Common and Agent users before they go live.</p>
                        </div>
                    </div>

                    {error ? <div className="feedback-banner error-banner">{error}</div> : null}

                    <div className="account-grid account-grid--stats">
                        <article className="account-card">
                            <p className="account-card-label">Pending Listings</p>
                            <h3>{queueSummary.pending}</h3>
                        </article>
                        <article className="account-card">
                            <p className="account-card-label">Submitters</p>
                            <h3>{queueSummary.submitters}</h3>
                        </article>
                        <article className="account-card">
                            <p className="account-card-label">Average Price</p>
                            <h3>₹ {queueSummary.averagePrice.toLocaleString("en-IN")}</h3>
                        </article>
                    </div>

                    <article className="account-card">
                        {loading ? (
                            <p className="account-muted">Loading pending properties...</p>
                        ) : rows.length === 0 ? (
                            <div className="account-empty">
                                <h3>No pending properties</h3>
                                <p>The approval queue is clear. New submissions will appear here automatically.</p>
                            </div>
                        ) : (
                            <div className="account-table-wrap">
                                <table className="account-table">
                                    <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Submitted By</th>
                                        <th>Type</th>
                                        <th>City</th>
                                        <th>Price</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.title}</td>
                                            <td>{row.owner?.full_name || row.owner?.email || `User ${row.owner_id}`}</td>
                                            <td>{row.type}</td>
                                            <td>{row.city || "-"}</td>
                                            <td>₹ {Number(row.price || row.min_price || row.max_price || 0).toLocaleString("en-IN")}</td>
                                            <td>
                                                <span className={`account-status ${row.status}`}>{row.status}</span>
                                            </td>
                                            <td>{row.is_verified ? "✅ Yes" : "—"}</td>
                                            <td>
                                                <div className="action-group">
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => setDialog({type: "approve", row})}
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        disabled={row.is_verified}
                                                        onClick={() => runAction(() => verifyProperty(row.id))}
                                                    >
                                                        {row.is_verified ? "✅ Verified" : "Verify"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => setDialog({type: "reject", row})}
                                                    >
                                                        Reject
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => openEditDialog(row)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => setDialog({type: "delete", row})}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </article>

                    <article className="account-card">
                        <div className="page-header">
                            <div>
                                <h3>Public Inquiries</h3>
                                <p className="page-subtitle">Admin reviews inquiry leads before sharing them with the
                                    listing publisher.</p>
                            </div>
                        </div>

                        {loading ? (
                            <p className="account-muted">Loading inquiries...</p>
                        ) : publicInquiries.length === 0 ? (
                            <div className="account-empty">
                                <h3>No public inquiries</h3>
                                <p>New buyer or tenant enquiry leads will appear here for admin review.</p>
                            </div>
                        ) : (
                            <div className="account-table-wrap">
                                <table className="account-table">
                                    <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Lead</th>
                                        <th>Phone</th>
                                        <th>Publisher</th>
                                        <th>Budget</th>
                                        <th>Status</th>
                                        <th>Verified</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {publicInquiries.map((lead) => (
                                        <tr key={lead.id}>
                                            <td>
                                                <strong>{lead.property_title}</strong>
                                                <div className="account-table-subtext">#{lead.property_id}</div>
                                            </td>
                                            <td>
                                                <strong>{lead.name}</strong>
                                                <div
                                                    className="account-table-subtext">{lead.message || "No message"}</div>
                                            </td>
                                            <td>{lead.phone}</td>
                                            <td>
                                                <strong>{lead.publisher_name}</strong>
                                                <div className="account-table-subtext">{lead.publisher_email}</div>
                                            </td>
                                            <td>{lead.budget ? `₹ ${Number(lead.budget).toLocaleString("en-IN")}` : "-"}</td>
                                            <td>
                          <span className={`account-status ${lead.is_shared_with_publisher ? "published" : "pending"}`}>
                            {lead.is_shared_with_publisher ? "shared" : "awaiting review"}

                          </span>
                                            </td>

                                            <td>
                                                <div className="action-group">
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        disabled={lead.is_shared_with_publisher}
                                                        onClick={() => runAction(() => sharePublicInquiryWithPublisher(lead.id))}
                                                    >
                                                        {lead.is_shared_with_publisher ? "Shared" : "Share with Publisher"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </article>
                </section>
            </div>

            {dialog ? (
                <Modal className="fy-modal--compact account-dialog" onClose={closeDialog}>
                    {dialog.type === "edit" ? (
                        <>
                            <div className="fy-modal-head">
                                <h3>Edit Listing</h3>
                            </div>
                            <p className="account-dialog-copy">
                                Update the property information before deciding whether it should go live.
                            </p>
                            <div className="account-dialog-meta">
                                <span>Type: {dialog.row.type}</span>
                                <span>
                  Submitted by: {dialog.row.owner?.full_name || dialog.row.owner?.email || `User ${dialog.row.owner_id}`}
                </span>
                                <span>Status: {dialog.row.status}</span>
                            </div>

                            <label className="fy-label" htmlFor="admin-edit-title">
                                Listing Title
                            </label>
                            <input
                                id="admin-edit-title"
                                value={editForm.title}
                                onChange={(event) => setEditForm((prev) => ({...prev, title: event.target.value}))}
                            />

                            <label className="fy-label" htmlFor="admin-edit-city">
                                City
                            </label>
                            <input
                                id="admin-edit-city"
                                value={editForm.city}
                                onChange={(event) => setEditForm((prev) => ({...prev, city: event.target.value}))}
                            />

                            <label className="fy-label" htmlFor="admin-edit-price">
                                Price
                            </label>
                            <input
                                id="admin-edit-price"
                                type="number"
                                value={editForm.price}
                                onChange={(event) => setEditForm((prev) => ({...prev, price: event.target.value}))}
                            />

                            <div className="account-dialog-actions">
                                <button type="button" className="btn btn-secondary" onClick={closeDialog}
                                        disabled={submitting}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-strong" onClick={saveEdit}
                                        disabled={submitting}>
                                    {submitting ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="fy-modal-head">
                                <h3>
                                    {dialog.type === "approve"
                                        ? "Approve Listing"
                                        : dialog.type === "reject"
                                            ? "Reject Listing"
                                            : "Delete Listing"}
                                </h3>
                            </div>
                            <p className="account-dialog-copy">{getConfirmationCopy(dialog)}</p>
                            <div className="account-dialog-meta">
                                <span>City: {dialog.row.city || "-"}</span>
                                <span>Price: ₹ {Number(dialog.row.price || dialog.row.min_price || dialog.row.max_price || 0).toLocaleString("en-IN")}</span>
                                <span>Status: {dialog.row.status}</span>
                            </div>

                            <div className="account-dialog-actions">
                                <button type="button" className="btn btn-secondary" onClick={closeDialog}
                                        disabled={submitting}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-strong" onClick={confirmAction}
                                        disabled={submitting}>
                                    {submitting
                                        ? "Processing..."
                                        : dialog.type === "approve"
                                            ? "Confirm Approve"
                                            : dialog.type === "reject"
                                                ? "Confirm Reject"
                                                : "Confirm Delete"}
                                </button>
                            </div>
                        </>
                    )}
                </Modal>
            ) : null}
        </div>
    );
}
